import React, { type MouseEvent } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import { useFragment, graphql } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Avatar from '@atlaskit/avatar';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import { colors, fontSizeSmall, typography } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Link from '@atlassian/jira-common-analytics-v2-wrapped-components/src/link';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { summaryCell_softwareCodePage$key } from '@atlassian/jira-relay/src/__generated__/summaryCell_softwareCodePage.graphql';
import {
	SUMMARY_CELL_TITLE_LINK_TEST_ID,
	SUMMARY_CELL_TARGET_BRANCH_LINK_TEST_ID,
	SUMMARY_CELL_REPOSITORY_LINK_TEST_ID,
} from '../../../../../../../../common/constants';
import messages from './messages';

const DotSeparator = () => <SeparatorWrapper>•</SeparatorWrapper>;

type SummaryCellProps = {
	summaryCellData: summaryCell_softwareCodePage$key;
};

const SummaryCell = ({ summaryCellData }: SummaryCellProps) => {
	const data = useFragment(
		graphql`
			fragment summaryCell_softwareCodePage on DevOpsPullRequestDetails {
				title @required(action: THROW)
				url
				author {
					name
					picture
				}
				destinationBranch {
					name
					url
				}
				pullRequestInternalId
				repositoryName
				repositoryUrl
			}
		`,
		summaryCellData,
	);
	const { formatMessage } = useIntl();

	const handleClick =
		(actionSubjectId: string) =>
		(_: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, actionSubjectId);
		};

	return (
		<Wrapper>
			<Avatar size="medium" src={data.author?.picture || ''} />
			<MainContent>
				<HeaderRow>
					{data.url ? (
						<PullRequestTitleLink
							href={data.url}
							target="_blank"
							rel="noopener noreferrer"
							title={data.title}
							// @ts-expect-error - Type '(_: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => void' is not assignable to type 'MouseEventHandler<HTMLElement> | MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGSymbolElement> | ... 115 more ... | undefined'.
							onClick={handleClick('pullRequestListItem')}
							data-testid={SUMMARY_CELL_TITLE_LINK_TEST_ID}
						>
							{data.title}
						</PullRequestTitleLink>
					) : (
						<PullRequestTitleText title={data.title}>{data.title}</PullRequestTitleText>
					)}
					{data.destinationBranch && (
						<TargetBranchWrapper>
							<ArrowContainer>
								<ArrowRightIcon
									size="small"
									label=""
									primaryColor={token('color.icon', colors.N800)}
								/>
							</ArrowContainer>
							{data.destinationBranch.url ? (
								<TargetBranchLink
									href={data.destinationBranch.url || ''}
									target="_blank"
									// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
									title={data.destinationBranch.name}
									rel="noopener noreferrer"
									// @ts-expect-error - Type '(_: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => void' is not assignable to type 'MouseEventHandler<HTMLElement> | MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGSymbolElement> | ... 115 more ... | undefined'.
									onClick={handleClick('pullRequestListTargetBranch')}
									data-testid={SUMMARY_CELL_TARGET_BRANCH_LINK_TEST_ID}
								>
									{data.destinationBranch.name}
								</TargetBranchLink>
							) : (
								<TargetBranchText title={data.destinationBranch.name ?? ''}>
									{data.destinationBranch.name}
								</TargetBranchText>
							)}
						</TargetBranchWrapper>
					)}
				</HeaderRow>
				<SecondaryRow>
					{data.author?.name && <>{data.author?.name}</>}
					{data.author?.name && data.pullRequestInternalId && <DotSeparator />}
					{data.pullRequestInternalId && <>#{data.pullRequestInternalId}</>}
					{data.pullRequestInternalId && data.repositoryName && <DotSeparator />}
					{data.repositoryName && (
						<>
							{formatMessage(messages.repositoryPrefix)}:&nbsp;
							<RepositoryName
								// @ts-expect-error - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
								href={data.repositoryUrl}
								target="_blank"
								rel="noopener noreferrer"
								// @ts-expect-error - Type '(_: MouseEvent<HTMLAnchorElement>, analyticsEvent: UIAnalyticsEvent) => void' is not assignable to type 'MouseEventHandler<HTMLElement> | MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGSymbolElement> | ... 115 more ... | undefined'.
								onClick={handleClick('pullRequestListRepo')}
								data-testid={SUMMARY_CELL_REPOSITORY_LINK_TEST_ID}
							>
								{data.repositoryName}
							</RepositoryName>
						</>
					)}
				</SecondaryRow>
			</MainContent>
		</Wrapper>
	);
};

export default SummaryCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainContent = styled2.div({
	display: 'flex',
	maxWidth: '100%',
	flexDirection: 'column',
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderRow = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexWrap: 'wrap',
	maxWidth: '40vw',
	rowGap: token('space.050', '4px'),
	columnGap: token('space.025', '2px'),
	marginBottom: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: typography.h400().fontSize,
});

const PullRequestTitleSharedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N900),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PullRequestTitleText = styled.div`
	${PullRequestTitleSharedStyles}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondaryRow = styled2.div({
	display: 'flex',
	flexDirection: 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall}px`,
	flexWrap: 'wrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArrowContainer = styled2.div({
	alignItems: 'center',
	display: 'flex',
	marginRight: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SeparatorWrapper = styled2.div({
	margin: `0 ${token('space.025', '2px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TargetBranchWrapper = styled2.div({
	display: 'flex',
	maxWidth: 'inherit',
});

const TargetBranchSharedStyles = css({
	display: 'inline-block',

	font: token('font.body.small'),
	fontWeight: token('font.weight.semibold'),
	padding: `${token('space.025', '2px')} ${token('space.050', '4px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N300),
	borderRadius: token('space.050', '4px'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TargetBranchText = styled.div`
	${TargetBranchSharedStyles}
`;

// @ts-expect-error - No overload matches this call.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PullRequestTitleLink = styled(Link)(
	{
		transition: '0.2s ease-out',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	PullRequestTitleSharedStyles,
);

// @ts-expect-error - No overload matches this call.
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TargetBranchLink = styled(Link)`
	${TargetBranchSharedStyles}
	transition: 0.2s ease-out;
	:hover,
	:focus {
		background-color: ${token('color.background.accent.gray.subtler', colors.N30)};
		color: ${token('color.text.subtlest', colors.N300)};
	}
`;

// @ts-expect-error - No overload matches this call.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositoryName = styled(Link)(
	{
		fontWeight: 500,
		transition: '0.2s ease-out',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtlest', colors.N300),
	},
	// @ts-expect-error - Property 'href' does not exist on type 'ThemeProps<any>'.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ href }) =>
		!href?.length &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			pointerEvents: 'none',
		}),
);
